/**
 * LP Email Popup
 * - Desktop: centered dialog. Mobile: bottom sheet with swipe-to-dismiss.
 * - Opens after a delay or on exit intent; suppressed after dismissal
 *   (for N days) or subscription (365 days) via localStorage.
 * - Submits the customer form over fetch so the modal can swap to the
 *   success state without a reload; falls back to a native submit when
 *   Shopify responds with a captcha challenge. In that flow the form's
 *   return_to brings the visitor back with ?lp-signup=1, which reopens
 *   the dialog directly in the success state.
 */
(() => {
  const STORAGE_KEY = 'lp-email-popup';
  const DAY_MS = 86400000;

  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  };

  const writeState = (patch) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch {
      /* private browsing */
    }
  };

  class LpEmailPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.form = this.querySelector('form');
      this.signupPanel = this.querySelector('[data-lp-popup-signup]');
      this.successPanel = this.querySelector('[data-lp-popup-success]');
      this.errorMessage = this.querySelector('[data-lp-popup-error]');
      this.designMode = this.dataset.designMode === 'true';
      this.opened = false;

      this.bindClose();
      this.bindSubmit();
      this.bindCopy();
      this.bindDrag();

      if (this.designMode) {
        this.bindDesignMode();
        return;
      }

      if (new URLSearchParams(location.search).has('lp-signup')) {
        const url = new URL(location.href);
        url.searchParams.delete('lp-signup');
        history.replaceState({}, '', url);
        this.showSuccess();
        this.open();
        return;
      }

      if (!this.isEligible()) return;
      this.scheduleTriggers();
    }

    isEligible() {
      const state = readState();
      if (state.subscribedAt && Date.now() - state.subscribedAt < 365 * DAY_MS) return false;
      const dismissDays = parseInt(this.dataset.dismissDays, 10) || 30;
      if (state.dismissedAt && Date.now() - state.dismissedAt < dismissDays * DAY_MS) return false;
      return true;
    }

    scheduleTriggers() {
      const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);

      if (this.dataset.exitIntent === 'true' && matchMedia('(pointer: fine)').matches) {
        this.onExitIntent = (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.open();
        };
        document.addEventListener('mouseout', this.onExitIntent);
      }
    }

    open() {
      if (this.opened || this.dialog.open) return;
      this.opened = true;
      clearTimeout(this.timer);
      if (this.onExitIntent) document.removeEventListener('mouseout', this.onExitIntent);

      this.dialog.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
      document.dispatchEvent(new CustomEvent('lp-popup:open'));
    }

    close(reason) {
      if (this.dialog.open) this.dialog.close();
      if (reason === 'dismissed' && !this.designMode) writeState({ dismissedAt: Date.now() });
    }

    bindClose() {
      this.querySelectorAll('[data-lp-popup-close]').forEach((button) => {
        button.addEventListener('click', () => this.close('dismissed'));
      });

      // Escape key fires 'cancel'; 'close' always fires when the dialog closes.
      this.dialog.addEventListener('cancel', () => {
        if (!this.designMode) writeState({ dismissedAt: Date.now() });
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.removeAttribute('scroll-lock');
      });

      // A click on the ::backdrop targets the dialog element itself.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close('dismissed');
      });
    }

    bindSubmit() {
      if (!this.form) return;
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submitButton = this.form.querySelector('[type="submit"]');
        submitButton.disabled = true;
        if (this.errorMessage) this.errorMessage.hidden = true;

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
          });

          if (response.url && response.url.includes('/challenge')) {
            this.form.submit();
            return;
          }
          if (!response.ok && response.status !== 302) throw new Error(String(response.status));

          this.showSuccess();
          document.dispatchEvent(new CustomEvent('lp-popup:subscribed'));
        } catch {
          submitButton.disabled = false;
          if (this.errorMessage) this.errorMessage.hidden = false;
        }
      });
    }

    showSuccess() {
      if (!this.designMode) writeState({ subscribedAt: Date.now() });
      if (this.signupPanel) this.signupPanel.hidden = true;
      if (this.successPanel) {
        this.successPanel.hidden = false;
        this.successPanel.querySelector('.lp-popup__offer--success')?.focus();
      }
    }

    bindCopy() {
      const copyButton = this.querySelector('[data-lp-popup-copy]');
      if (!copyButton) return;
      const label = copyButton.querySelector('[data-lp-popup-copy-label]');
      copyButton.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(copyButton.dataset.code);
          label.textContent = 'Copied ✓';
          setTimeout(() => (label.textContent = 'Copy code'), 2000);
        } catch {
          /* clipboard unavailable — code is still visible to copy manually */
        }
      });
    }

    bindDrag() {
      const handle = this.querySelector('.lp-popup__handle');
      if (!handle) return;
      let startY = 0;

      handle.addEventListener('pointerdown', (event) => {
        startY = event.clientY;
        handle.setPointerCapture(event.pointerId);
        this.dialog.style.transition = 'none';
      });

      handle.addEventListener('pointermove', (event) => {
        if (!handle.hasPointerCapture(event.pointerId)) return;
        const dy = Math.max(0, event.clientY - startY);
        this.dialog.style.transform = `translateY(${dy}px)`;
      });

      handle.addEventListener('pointerup', (event) => {
        handle.releasePointerCapture(event.pointerId);
        const dy = Math.max(0, event.clientY - startY);
        this.dialog.style.transition = '';
        if (dy > 90) {
          this.close('dismissed');
          this.dialog.style.transform = '';
        } else {
          this.dialog.style.transform = '';
        }
      });
    }

    bindDesignMode() {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          this.opened = false;
          this.open();
        }
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.close();
      });
    }
  }

  if (!customElements.get('lp-email-popup')) {
    customElements.define('lp-email-popup', LpEmailPopup);
  }
})();
